import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { holidayAPI } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useBranch } from '../../context/BranchContext';
import HolidayFormModal from '../../components/modals/HolidayFormModal';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

export default function HolidayManagementPage() {
  const { branches, selectedBranch } = useBranch();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        year,
        ...(selectedBranch?.id ? { branch_id: selectedBranch.id } : {}),
      };
      const response = await holidayAPI.getHolidays(params);
      const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setHolidays(list);
    } catch (error) {
      console.error('Cannot fetch holidays', error);
      toast.error(error?.message || 'Không tải được danh sách ngày lễ');
    } finally {
      setLoading(false);
    }
  }, [year, selectedBranch]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleCreate = () => {
    setEditingHoliday(null);
    setShowFormModal(true);
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setShowFormModal(true);
  };

  const handleDelete = async (holiday) => {
    if (!window.confirm(`Bạn có chắc muốn xóa ngày lễ "${holiday.name}"?\n\nThao tác này có thể ảnh hưởng đến cách tính lương.`)) {
      return;
    }

    try {
      await holidayAPI.deleteHoliday(holiday.id);
      toast.success('Đã xóa ngày lễ thành công');
      fetchHolidays();
    } catch (error) {
      console.error('Cannot delete holiday', error);
      toast.error(error?.message || 'Không thể xóa ngày lễ');
    }
  };

  const handleToggleActive = async (holiday) => {
    try {
      await holidayAPI.updateHoliday(holiday.id, {
        is_active: !holiday.is_active,
      });
      toast.success('Đã cập nhật trạng thái ngày lễ');
      fetchHolidays();
    } catch (error) {
      console.error('Cannot update holiday', error);
      toast.error(error?.message || 'Không thể cập nhật ngày lễ');
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingHoliday(null);
    fetchHolidays();
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">Quản lý ngày lễ</h3>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <Card className="shadow-sm mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Bộ lọc</h5>
                <Button variant="primary" onClick={handleCreate}>
                  + Thêm ngày lễ
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col lg={4}>
                  <Form.Label>Năm</Form.Label>
                  <Form.Control
                    type="number"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    min={2020}
                    max={2100}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Danh sách ngày lễ</h5>
            </Card.Header>
            <Card.Body>
              {loading && (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              )}

              {!loading && holidays.length === 0 && (
                <div className="text-center py-4 text-muted">Không có dữ liệu</div>
              )}

              {!loading && holidays.length > 0 && (
                <div className="alert alert-info mb-3">
                  <strong>⚠️ Lưu ý:</strong> Ngày lễ sẽ làm tăng lương các ca trùng ngày (salary multiplier x2.0 mặc định).
                </div>
              )}

              {!loading && holidays.length > 0 && (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Ngày</th>
                      <th>Tên ngày lễ</th>
                      <th>Loại</th>
                      <th>Chi nhánh</th>
                      <th>Trạng thái</th>
                      <th>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holidays.map((holiday) => (
                      <tr key={holiday.id}>
                        <td>
                          <strong>{formatDate(holiday.date)}</strong>
                          {holiday.month && holiday.day && (
                            <div className="small text-muted">
                              ({holiday.day}/{holiday.month})
                            </div>
                          )}
                        </td>
                        <td>{holiday.name}</td>
                        <td>
                          {holiday.is_fixed ? (
                            <Badge bg="info">Cố định theo ngày</Badge>
                          ) : (
                            <Badge bg="secondary">Cố định theo thứ</Badge>
                          )}
                        </td>
                        <td>
                          {holiday.branch_id ? (
                            <Badge bg="primary">
                              {branches.find((b) => b.id === holiday.branch_id)?.name || holiday.branch_id}
                            </Badge>
                          ) : (
                            <Badge bg="success">Tất cả</Badge>
                          )}
                        </td>
                        <td>
                          {holiday.is_active ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleEdit(holiday)}
                            >
                              Sửa
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleToggleActive(holiday)}
                            >
                              {holiday.is_active ? 'Vô hiệu' : 'Kích hoạt'}
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger"
                              onClick={() => handleDelete(holiday)}
                            >
                              Xóa
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showFormModal && (
        <HolidayFormModal
          show={showFormModal}
          onHide={() => {
            setShowFormModal(false);
            setEditingHoliday(null);
          }}
          holiday={editingHoliday}
          onSuccess={handleFormSuccess}
        />
      )}
    </PageLayout>
  );
}

